import React, { FC } from 'react';
import { Button } from '@clearcover/atlas';
import { CalculatePaymentPlanResponse } from '@clearcover/core-app-types';
import { formatMoney } from 'Shared/utils/number/number';
import styles from './styles.module.css';

export type CustomFormModalFooterProps = {
  paymentPlan: CalculatePaymentPlanResponse | null;
  isRenewalPlanChange: boolean;
  close: () => void;
  isLoading: boolean;
  isFormIncomplete?: boolean;
  onContinue?: () => void;
};

export const CustomFormModalFooter: FC<CustomFormModalFooterProps> = ({
  paymentPlan,
  isRenewalPlanChange,
  close,
  isLoading,
  isFormIncomplete = false,
  onContinue,
}) => {
  const calculateFuturePayments = () => {
    if (!paymentPlan || !paymentPlan.installments.length) {
      return { value: 0, currency: 'USD' };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const filteredInstallments = paymentPlan.installments.filter(
      (installment: CalculatePaymentPlanResponse['installments'][0]) =>
        installment.status === 'DUE' &&
        installment.type === 'INSTALLMENT' &&
        installment.dueDate &&
        new Date(installment.dueDate) > today
    );

    const sortedInstallments = filteredInstallments.sort(
      (
        a: CalculatePaymentPlanResponse['installments'][0],
        b: CalculatePaymentPlanResponse['installments'][0]
      ) => a.sequenceNumber - b.sequenceNumber
    );

    let futureInstallment;

    // See: https://github.com/Clearcover/cc-typescript/pull/3257/ (fix for mobile)
    // If there are more than 2 remaining installments we want to take the second-to-last installment
    // as the future payment amount as the final installment may have extra pennies added on.
    if (sortedInstallments.length > 2) {
      futureInstallment = sortedInstallments[sortedInstallments.length - 2];
      // If there are exactly 1 or 2 installments remaining we want to take the last installment.
      // In the case of 1 installment, the 'last' installment is the only installment.
      // In the case of 2 installments, the last installment is the guarenteed to not have its
      // balance and amount be different. If endorsements were made to a policy it's possible that
      // the current installment could have a different amount vs balance amount.
    } else if (
      sortedInstallments.length === 1 ||
      sortedInstallments.length === 2
    ) {
      futureInstallment = sortedInstallments[sortedInstallments.length - 1];
    }

    return {
      value: futureInstallment?.balance.value || 0,
      currency:
        futureInstallment?.balance.currency ||
        paymentPlan.installments[0]?.balance.currency ||
        'USD',
    };
  };

  const calculateDownPayment = () => {
    if (!paymentPlan || !paymentPlan.installments.length) {
      return { value: 0, currency: 'USD' };
    }

    const downPayment = paymentPlan.installments.find(
      (installment: CalculatePaymentPlanResponse['installments'][0]) =>
        installment.type === 'DOWNPAYMENT'
    );

    return {
      value: downPayment?.balance.value || 0,
      currency:
        downPayment?.balance.currency ||
        paymentPlan.installments[0]?.balance.currency ||
        'USD',
    };
  };

  return (
    <div className={styles.customFooter}>
      <Button variant="secondary" onClick={close}>
        Cancel
      </Button>

      {paymentPlan ? (
        <div className={styles.paymentSummary}>
          {isRenewalPlanChange ? (
            <div className={styles.paymentSummaryItem}>
              <div className={styles.paymentLabel}>Down payment</div>
              <div
                className={
                  isLoading ? styles.loadingPlaceholder : styles.paymentValue
                }
              >
                {!isLoading &&
                  (isFormIncomplete
                    ? '$_'
                    : formatMoney(calculateDownPayment().value))}
              </div>
            </div>
          ) : (
            <div className={styles.paymentSummaryItem}>
              <div className={styles.paymentLabel}>Due today</div>
              <div
                className={
                  isLoading ? styles.loadingPlaceholder : styles.paymentValue
                }
              >
                {!isLoading &&
                  (isFormIncomplete
                    ? '$_'
                    : formatMoney(paymentPlan.amountDueToday?.value ?? 0))}
              </div>
            </div>
          )}
          <div className={styles.paymentSeparator}></div>
          <div className={styles.paymentSummaryItem}>
            <div className={styles.paymentLabel}>Future payments</div>
            <div
              className={
                isLoading ? styles.loadingPlaceholder : styles.paymentValue
              }
            >
              {!isLoading &&
                (isFormIncomplete
                  ? '$_'
                  : formatMoney(calculateFuturePayments().value))}
            </div>
          </div>
        </div>
      ) : null}

      <Button
        variant="primary"
        onClick={onContinue}
        disabled={isFormIncomplete || isLoading || !paymentPlan}
      >
        Continue
      </Button>
    </div>
  );
};
